import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaSearch,
  FaTimes,
  FaCalendarAlt,
  FaBox,
} from "react-icons/fa";
import "./style.css";

const AddProduct = ({ setShouldRefetch }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, userData } = useContext(UserContext);

  // Check if we're in edit mode or from inventory
  const productToEdit = location.state?.product;
  const isEditMode = !!productToEdit;
  const fromInventory = location.state?.checkboxChecked || false;
  const fromWarehouse = location.state?.fromWarehouse || false;
  const preselectedWarehouseId = location.state?.warehouseId;

  const [formData, setFormData] = useState({
    nameAr: "",
    nameEn: "",
    registrationNumber: "",
    producer: "",
    unit: "",
    details: "",
    initialBalance: "",
    price: "",
    threshold: "",
    imageFiles: [],
    tenantId: "",
    farmId: "",
    typeId: "",
    addToInventory: location.state?.checkboxChecked || false,
    warehouseId: preselectedWarehouseId || "",
    movementDate: new Date().toISOString().split("T")[0],
    expirationDate: "",
  });

  const [inventoryTypes, setInventoryTypes] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typeSearchOpen, setTypeSearchOpen] = useState(false);
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [existingImageUrls, setExistingImageUrls] = useState([]);

  useEffect(() => {
    if (userData && userData.Employee) {
      const employee = userData.Employee;
      setFormData((prev) => ({
        ...prev,
        tenantId: employee.tenantId,
        farmId: employee.farmId,
      }));
    }
    console.log("product to edit", productToEdit);
    // If in edit mode, populate form with existing product data
    if (isEditMode && productToEdit) {
      setFormData((prev) => ({
        ...prev,
        nameAr: productToEdit.nameAr || productToEdit.name || "",
        nameEn: productToEdit.nameEn || productToEdit.name || "",
        registrationNumber:
          productToEdit.registrationNumber ||
          productToEdit.registerNumber ||
          "",
        producer: productToEdit.producer || "",
        unit: productToEdit.unit || "",
        details: productToEdit.details || productToEdit.description || "",
        typeId: productToEdit.typeId || productToEdit.type?.id || "",
        threshold: productToEdit.threshold ?? "",
        expirationDate:
          productToEdit.expireDate || productToEdit.expirationDate
            ? new Date(productToEdit.expireDate || productToEdit.expirationDate)
                .toISOString()
                .split("T")[0]
            : "",
        initialBalance: productToEdit.initialBalance ?? "",
        price: productToEdit.price ?? "",
        warehouseId: productToEdit.locationId || "",
        movementDate: productToEdit.date
          ? new Date(productToEdit.date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      }));

      // Set selected type if available
      if (productToEdit.type) {
        setSelectedType(productToEdit.type);
      }

      // Note: Warehouse/location is set via warehouseId in formData

      // Load existing images
      if (productToEdit.imagesUrls) {
        const urls = productToEdit.imagesUrls
          .split(",")
          .filter((url) => url.trim());
        setExistingImageUrls(urls);
      }
    }
  }, [userData, isEditMode, productToEdit]);

  useEffect(() => {
    const fetchInventoryTypes = async () => {
      if (!formData.farmId) return;
      try {
        const response = await axios.get(
          `api/inventoryType/findByFarmId/${formData.farmId}`,
          {
            headers: {
              Authorization: token,
            },
          }
        );
        if (Array.isArray(response.data.items)) {
          setInventoryTypes(response.data.items);

          // Set selected type if in edit mode
          if (isEditMode && productToEdit) {
            const typeId = productToEdit.typeId || productToEdit.type?.id;
            if (typeId) {
              const matchingType = response.data.items.find(
                (t) => t.id === typeId
              );
              if (matchingType) {
                setSelectedType(matchingType);
                setFormData((prev) => ({ ...prev, typeId: matchingType.id }));
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching inventory types:", error);
        toast.error(t("Error fetching inventory types."));
      }
    };

    const fetchInventoryLocations = async () => {
      if (!formData.farmId) return;
      try {
        const response = await axios.get(
          `api/inventoryLocation/findByFarmId/${formData.farmId}`,
          {
            headers: {
              Authorization: token,
            },
          }
        );
        if (Array.isArray(response.data.items)) {
          setInventoryLocations(response.data.items);

          // Set warehouse ID if in edit mode
          if (isEditMode && productToEdit?.locationId) {
            const matchingLocation = response.data.items.find(
              (l) => l.id === productToEdit.locationId
            );
            if (matchingLocation) {
              setFormData((prev) => ({
                ...prev,
                warehouseId: matchingLocation.id,
              }));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching inventory locations:", error);
        toast.error(t("Error fetching inventory locations."));
      }
    };

    fetchInventoryTypes();
    fetchInventoryLocations();
  }, [formData.farmId, token, t, isEditMode, productToEdit]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...files],
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      imageFiles: prev.imageFiles.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData((prev) => ({ ...prev, typeId: type.id }));
    setTypeSearchOpen(false);
    setTypeSearchQuery("");
  };

  const handleWarehouseChange = (e) => {
    const warehouseId = e.target.value;
    setFormData((prev) => ({ ...prev, warehouseId }));
  };

  const validateForm = () => {
    const errors = {};

    // Basic product validation
    if (!formData.nameAr && !formData.nameEn) {
      errors.name = t("Product name is required");
    }

    if (!selectedType) {
      errors.type = t("Product type is required");
    }

    if (!formData.unit) {
      errors.unit = t("Unit is required");
    }

    // Stock validation only when adding to inventory (not in edit mode without addToInventory)
    if (formData.addToInventory) {
      if (!formData.warehouseId) {
        errors.warehouse = t("Warehouse is required when adding to inventory");
      }

      if (
        !formData.initialBalance ||
        parseFloat(formData.initialBalance) <= 0
      ) {
        errors.initialBalance = t("Initial balance must be greater than 0");
      }

      if (!formData.movementDate) {
        errors.movementDate = t("Movement date is required");
      }

      // Only require price for new stock additions
      if (!formData.price) {
        errors.price = t("Price is required when adding to inventory");
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e, retryCount = 0) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      const productData = {
        nameAr: formData.nameAr || formData.nameEn || "Product", // Ensure never empty
        nameEn: formData.nameEn || formData.nameAr || "Product", // Ensure never empty
        registrationNumber: formData.registrationNumber || "",
        details: formData.details || "",
        producer: formData.producer || "",
        unit: formData.unit,
        tenantId: parseInt(formData.tenantId),
        farmId: parseInt(formData.farmId),
        typeId: parseInt(formData.typeId),
        threshold: formData.threshold ? parseFloat(formData.threshold) : 0, // Always send threshold, default to 0
        expirationDate: formData.expirationDate
          ? new Date(formData.expirationDate).getTime()
          : null,
      };

      // Add stock-related fields only if adding to inventory
      if (formData.addToInventory) {
        productData.initialBalance = parseFloat(formData.initialBalance);
        productData.locationId = parseInt(formData.warehouseId);
      }

      // If editing, include the product ID and existing images
      if (isEditMode && productToEdit) {
        productData.id = productToEdit.id;
        // Update images URLs with remaining existing images
        if (existingImageUrls.length > 0) {
          productData.imagesUrls = existingImageUrls.join(",");
        }
      }

      // Create a Blob with proper content type for JSON but NO filename
      const productBlob = new Blob([JSON.stringify(productData)], {
        type: "application/json; charset=utf-8",
      });
      formDataToSend.append("product", productBlob);

      // Add image files with correct parameter name
      formData.imageFiles.forEach((file) => {
        formDataToSend.append("imageFile", file);
      });

      let productResponse;

      if (isEditMode) {
        // Update existing product
        for (let [key, value] of formDataToSend.entries()) {
          console.log(key, value);
        }
        productResponse = await axios.put(
          `api/inventoryItemDetails/updateProductWithImages`,
          formDataToSend,
          {
            headers: {
              Authorization: token,
            },
          }
        );
      } else {
        // Create new product
        productResponse = await axios.post(
          `api/inventoryItemDetails/addProductWithImages`,
          formDataToSend,
          {
            headers: {
              Authorization: token,
            },
          }
        );
      }

      // If adding to inventory, create procurement record
      if (formData.addToInventory && productResponse.data?.items?.[0]?.id) {
        const productId = productResponse.data.items[0].id;

        const procurementData = {
          inventoryItemDetailId: productId,
          tenantId: parseInt(formData.tenantId),
          farmId: parseInt(formData.farmId),
          procurementDate: new Date(formData.movementDate).getTime(),
          quantity: parseFloat(formData.initialBalance),
          price: parseFloat(formData.price),
          locationId: parseInt(formData.warehouseId),
        };

        await axios.post(`api/inventoryProcurement`, procurementData, {
          headers: {
            Authorization: token,
          },
        });
      }

      toast.success(
        isEditMode
          ? t("Product updated successfully!")
          : t("Product added successfully!")
      );

      if (setShouldRefetch) {
        setShouldRefetch(true);
      }

      // Navigate back with refresh flag
      navigate("../index", { state: { refresh: true } });
    } catch (error) {
      console.error("Error saving product:", error);

      // Retry logic for network errors
      if (!error.response && retryCount < 2) {
        setLoading(false);
        setTimeout(() => {
          handleSubmit(e, retryCount + 1);
        }, 1000 * (retryCount + 1));
        toast.error(t("Network error, retrying..."));
        return;
      }

      if (error.response) {
        const message =
          error.response.data?.message ||
          error.response.data?.error ||
          "Bad Request";
        toast.error(t(`Error: ${message}`));
      } else {
        toast.error(t("Error saving product. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (item) => {
    if (!item) return "";
    return i18n.language === "ar"
      ? item.nameAr || item.arabicName || item.name
      : item.nameEn || item.englishName || item.name || item.nameAr;
  };

  // Filter types for search
  const filteredTypes = inventoryTypes.filter((type) => {
    const name = getDisplayName(type).toLowerCase();
    return name.includes(typeSearchQuery.toLowerCase());
  });

  return (
    <div className="apf-container">
      <div className="apf-form-card">
        <div className="apf-form-header">
          <button
            className="apf-back-button"
            onClick={() => navigate(-1)}
            aria-label={t("Go back")}
            type="button"
          >
            <FaArrowLeft />
          </button>
          <div className="apf-header-content">
            <h1>{isEditMode ? t("Update Product") : t("Add Product")}</h1>
            <p>
              {isEditMode
                ? t("Modify product information")
                : t("Create new product in inventory")}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="apf-form-body">
            {/* Basic Information Section */}
            <div className="apf-form-section">
              <h2 className="apf-section-title">{t("Basic Information")}</h2>

              {/* Product Name Fields */}
              <div className="apf-form-row">
                <div
                  className={`apf-form-group ${
                    validationErrors.name ? "apf-error" : ""
                  }`}
                >
                  <label htmlFor="nameAr">
                    {t("Product Name (Arabic)")}{" "}
                    <span className="apf-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nameAr"
                    name="nameAr"
                    value={formData.nameAr}
                    onChange={handleInputChange}
                    placeholder={t("Enter product name in Arabic")}
                  />
                </div>

                <div
                  className={`apf-form-group ${
                    validationErrors.name ? "apf-error" : ""
                  }`}
                >
                  <label htmlFor="nameEn">
                    {t("Product Name (English)")}{" "}
                    <span className="apf-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nameEn"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleInputChange}
                    placeholder={t("Enter product name in English")}
                  />
                  {validationErrors.name && (
                    <span className="apf-error-message">
                      {validationErrors.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Type Selection */}
              <div
                className={`form-group full-width ${
                  validationErrors.type ? "apf-error" : ""
                }`}
              >
                <label htmlFor="type">
                  {t("Product Type")} <span className="apf-required">*</span>
                </label>
                <div className="apf-type-select-wrapper">
                  <input
                    type="text"
                    id="type"
                    value={selectedType ? getDisplayName(selectedType) : ""}
                    onClick={() => setTypeSearchOpen(true)}
                    readOnly
                    placeholder={t("Click to select product type")}
                    className="apf-type-select-input"
                  />
                  <button
                    type="button"
                    className="apf-type-search-btn"
                    onClick={() => setTypeSearchOpen(true)}
                    aria-label={t("Search types")}
                  >
                    <FaSearch />
                  </button>
                </div>
                {validationErrors.type && (
                  <span className="apf-error-message">
                    {validationErrors.type}
                  </span>
                )}
              </div>

              {/* Registration and Producer */}
              <div className="apf-form-row">
                <div className="apf-form-group">
                  <label htmlFor="registrationNumber">
                    {t("Registration Number")}
                  </label>
                  <input
                    type="text"
                    id="registrationNumber"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleInputChange}
                    placeholder={t("Enter registration number")}
                  />
                </div>

                <div className="apf-form-group">
                  <label htmlFor="producer">{t("Manufacturer")}</label>
                  <input
                    type="text"
                    id="producer"
                    name="producer"
                    value={formData.producer}
                    onChange={handleInputChange}
                    placeholder={t("Enter manufacturer name")}
                  />
                </div>
              </div>

              {/* Unit and Threshold */}
              <div className="apf-form-row">
                <div
                  className={`apf-form-group ${
                    validationErrors.unit ? "apf-error" : ""
                  }`}
                >
                  <label htmlFor="unit">
                    {t("Unit")} <span className="apf-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder={t("e.g., kg, liter, piece")}
                  />
                  {validationErrors.unit && (
                    <span className="apf-error-message">
                      {validationErrors.unit}
                    </span>
                  )}
                </div>

                <div className="apf-form-group">
                  <label htmlFor="expirationDate2">
                    {t("Expiration Date")}
                  </label>
                  <div className="apf-date-input-wrapper">
                    <input
                      type="date"
                      id="expirationDate2"
                      name="expirationDate"
                      value={formData.expirationDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    <FaCalendarAlt className="apf-date-icon" />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details Section */}
            <div className="apf-form-section">
              <h2 className="apf-section-title">{t("Additional Details")}</h2>

              {/* Description */}
              <div className="apf-form-group apf-full-width">
                <label htmlFor="details">{t("Product Description")}</label>
                <textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={t(
                    "Enter detailed product description, usage instructions, or notes..."
                  )}
                />
              </div>

              {/* Images */}
              <div className="apf-form-group apf-full-width">
                <label>{t("Product Images")}</label>
                <div className="apf-image-upload-section">
                  <input
                    type="file"
                    multiple
                    onChange={handleImageChange}
                    accept="image/*"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="apf-image-upload-label"
                  >
                    {t("Choose Images")}
                  </label>
                  <p
                    style={{
                      marginTop: "10px",
                      color: "#6c757d",
                      fontSize: "0.9rem",
                    }}
                  >
                    {t("or drag and drop images here")}
                  </p>
                </div>

                {/* Display existing images */}
                {existingImageUrls.length > 0 && (
                  <div className="apf-existing-images-section">
                    <h4
                      style={{
                        marginTop: "20px",
                        marginBottom: "10px",
                        fontSize: "0.95rem",
                        color: "#495057",
                      }}
                    >
                      {t("Current Images")}
                    </h4>
                    <div className="apf-image-preview">
                      {existingImageUrls.map((url, index) => (
                        <div
                          key={`existing-${index}`}
                          className="apf-image-item"
                        >
                          <img
                            src={url}
                            alt={`Existing ${index}`}
                            onError={(e) => {
                              e.target.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjUwIiB5PSI1MCIgc3R5bGU9ImZpbGw6I2FhYTtmb250LXNpemU6MTRweDsiPkltYWdlPC90ZXh0Pjwvc3ZnPg==";
                            }}
                          />
                          <p
                            className="apf-image-name"
                            style={{ fontSize: "0.85rem", color: "#28a745" }}
                          >
                            {t("Saved Image")} {index + 1}
                          </p>
                          <button
                            type="button"
                            className="apf-remove-image-btn"
                            onClick={() => handleRemoveExistingImage(index)}
                            aria-label={t("Remove existing image")}
                            title={t("Remove this image")}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Display new images to upload */}
                {formData.imageFiles.length > 0 && (
                  <div className="apf-new-images-section">
                    <h4
                      style={{
                        marginTop: "20px",
                        marginBottom: "10px",
                        fontSize: "0.95rem",
                        color: "#495057",
                      }}
                    >
                      {t("New Images to Upload")}
                    </h4>
                    <div className="apf-image-preview">
                      {formData.imageFiles.map((file, index) => (
                        <div key={`new-${index}`} className="apf-image-item">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index}`}
                          />
                          <p className="apf-image-name">{file.name}</p>
                          <button
                            type="button"
                            className="apf-remove-image-btn"
                            onClick={() => handleRemoveImage(index)}
                            aria-label={t("Remove image")}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stock Information section removed in edit mode */}

            {/* Add to Inventory Checkbox - Show in create mode or edit mode without stock */}
            {(!isEditMode ||
              (isEditMode && !productToEdit?.initialBalance)) && (
              <div className="apf-checkbox-wrapper">
                <label className="apf-checkbox-label">
                  <input
                    type="checkbox"
                    name="addToInventory"
                    checked={formData.addToInventory}
                    onChange={handleInputChange}
                    disabled={fromInventory}
                  />
                  <span>
                    {isEditMode
                      ? t("Add this product to inventory stock")
                      : t("Add product to inventory stock immediately")}
                    {fromInventory && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "0.85rem",
                          color: "#6c757d",
                        }}
                      >
                        ({t("Required when adding from inventory")})
                      </span>
                    )}
                  </span>
                </label>
              </div>
            )}

            {/* Inventory Fields - Show when adding to inventory in any mode */}
            {formData.addToInventory && (
              <div className="apf-inventory-section">
                <h2 className="apf-section-title">{t("Stock Information")}</h2>

                <div className="apf-form-row">
                  <div
                    className={`apf-form-group ${
                      validationErrors.warehouse ? "apf-error" : ""
                    }`}
                  >
                    <label htmlFor="warehouseId">
                      {t("Warehouse")} <span className="apf-required">*</span>
                      {fromWarehouse && (
                        <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: '#6c757d' }}>
                          ({t("Pre-selected from warehouse")})
                        </span>
                      )}
                    </label>
                    <select
                      id="warehouseId"
                      name="warehouseId"
                      value={formData.warehouseId}
                      onChange={handleWarehouseChange}
                      disabled={fromWarehouse}
                      style={fromWarehouse ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">{t("Select Warehouse")}</option>
                      {inventoryLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {getDisplayName(location)}
                        </option>
                      ))}
                    </select>
                    {validationErrors.warehouse && (
                      <span className="apf-error-message">
                        {validationErrors.warehouse}
                      </span>
                    )}
                  </div>

                  <div
                    className={`apf-form-group ${
                      validationErrors.initialBalance ? "apf-error" : ""
                    }`}
                  >
                    <label htmlFor="initialBalance">
                      {t("Initial Balance")}{" "}
                      <span className="apf-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="initialBalance"
                      name="initialBalance"
                      value={formData.initialBalance}
                      onChange={handleInputChange}
                      placeholder={t("0.00")}
                      step="any"
                    />
                    {validationErrors.initialBalance && (
                      <span className="apf-error-message">
                        {validationErrors.initialBalance}
                      </span>
                    )}
                  </div>
                </div>

                <div className="apf-form-row">
                  <div
                    className={`apf-form-group ${
                      validationErrors.price ? "apf-error" : ""
                    }`}
                  >
                    <label htmlFor="price">
                      {t("Price")} <span className="apf-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder={t("0.00")}
                      step="any"
                    />
                    {validationErrors.price && (
                      <span className="apf-error-message">
                        {validationErrors.price}
                      </span>
                    )}
                  </div>

                  <div
                    className={`apf-form-group ${
                      validationErrors.movementDate ? "apf-error" : ""
                    }`}
                  >
                    <label htmlFor="movementDate">
                      {t("Movement Date")}{" "}
                      <span className="apf-required">*</span>
                    </label>
                    <div className="apf-date-input-wrapper">
                      <input
                        type="date"
                        id="movementDate"
                        name="movementDate"
                        value={formData.movementDate}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split("T")[0]}
                      />
                      <FaCalendarAlt className="apf-date-icon" />
                    </div>
                    {validationErrors.movementDate && (
                      <span className="apf-error-message">
                        {validationErrors.movementDate}
                      </span>
                    )}
                  </div>
                </div>

                {/* Threshold Field - moved to inventory section */}
                <div className="apf-form-row">
                  <div className="apf-form-group">
                    <label htmlFor="threshold">
                      {t("Minimum Stock Alert")}
                    </label>
                    <input
                      type="number"
                      id="threshold"
                      name="threshold"
                      value={formData.threshold}
                      onChange={handleInputChange}
                      placeholder={t(
                        "Alert when stock falls below this quantity"
                      )}
                      step="any"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="apf-form-actions">
            <button
              type="button"
              className="apf-button-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="apf-button-primary"
              disabled={loading}
            >
              {loading
                ? t("Saving...")
                : isEditMode
                ? t("Update Product")
                : t("Save Product")}
            </button>
          </div>
        </form>
      </div>

      {/* Type Search Modal */}
      {typeSearchOpen && (
        <div
          className="apf-modal-overlay"
          onClick={() => setTypeSearchOpen(false)}
        >
          <div
            className="apf-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="apf-modal-header">
              <h2>{t("Select Product Type")}</h2>
              <button
                className="apf-modal-close"
                onClick={() => setTypeSearchOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="apf-modal-search">
              <input
                type="text"
                placeholder={t("Search types...")}
                value={typeSearchQuery}
                onChange={(e) => setTypeSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="apf-types-list">
              {filteredTypes.length === 0 ? (
                <div className="apf-no-types">{t("No types found")}</div>
              ) : (
                filteredTypes.map((type) => (
                  <div
                    key={type.id}
                    className="apf-type-item"
                    onClick={() => handleTypeSelect(type)}
                  >
                    <div className="apf-type-content">
                      <FaBox className="apf-type-icon" />
                      <div className="apf-type-name">
                        {getDisplayName(type)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProduct;
